<template>
  <div v-if="paymentLoading || payment" ref="container" class="payment details-container"
      :class="{ 'details-containerTs': data.brandNameFormatted === Brand.Travelsphere }">
    <h3 class="details-container__header"
        :class="{ 'details-container__headerTs': data.brandNameFormatted === Brand.Travelsphere }">{{data.payment.title}}</h3>

    <div v-if="paymentLoading" class="payment__content details-container__body">
      <PlaneLoader :brandName=data.brandNameFormatted />
    </div>

    <div v-if="payment" class="payment__content details-container__body">
      <!-- If user can pay for tour -->
      <template v-if="!isReservation">
        <!-- If form is visible -->
        <div v-show="!payment.summaryView">
          <div class="details-container__body-intro" v-html="data.payment.descriptionHtml"></div>

          <div class="payment__types">
            <Input
              name="amountToPay"
              :required="true"
              :options="amountsToPay"
              :optionsRadio="true"
              optionValueKey="value"
              optionLabelKey="label"
              :startingValue="payment.data.depositAmount"
              :vuexPath="vuexPath"
              :brand="data.brandNameFormatted"
            />
          </div>

          <Input
            name="cardTypeId"
            label="Payment card type"
            placeholder="Select card type"
            :required="true"
            :options="payment.data.cardTypes"
            optionValueKey="id"
            optionLabelKey="name"
            :vuexPath="vuexPath"
          />
        </div>

        <!-- If is summary view -->
        <div v-if="payment.summaryView">
          <button class="details-container__edit-button" :class="{ 'details-container__edit-buttonTs': data.brandNameFormatted === Brand.Travelsphere }" @click="edit" aria-label="Edit your details" title="Click to edit your details">Edit</button>
          <div class="passenger-summary">
            <div class="passenger-summary__field">
              <b class="passenger-summary__attribute">Payment amount</b>
              <div class="passenger-summary__value">{{currency(paymentData.amountToPay)}}</div>
            </div>
            <div class="passenger-summary__field">
              <b class="passenger-summary__attribute">Card type</b>
              <div class="passenger-summary__value">{{getCardName()}}</div>
            </div>
          </div>
        </div>        
      </template>

      <!-- If user cannot pay -->
      <div v-else class="error-message nb" :class="{'error-message--confirmed':showButton}">
        <div class="error-message__icon ni"></div>
        <div
          v-if="passengersHaveMobilityLimitations"
          class="error-message__text"
          v-html="data.payment.medicalWarning"
        ></div>
        <div v-else class="error-message__text" v-html="data.payment.generalWarning"></div>        
      </div>

      <!-- Save and continue button -->
      <div class="details-container__footer" v-show="showButton">
        <button class="btn details-container__submit" :class="{ 'btn_Ts': data.brandNameFormatted === Brand.Travelsphere }" @click="save" aria-label="Save and continue" title="Click to save and continue">Save and continue</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Input from '@/vue/components/Form/Input.vue';
import PlaneLoader from './PlaneLoader.vue';
import { formatDate, scrollToElement } from '@/utils';
import {Brand} from './../../../../brand.js';

export default {
  name: 'Payment',

  components: {
    Input,
    PlaneLoader
  },

  data() {
    return {
      Brand,
      vuexPath: ['checkout', 'payment']
    };
  },

  computed: {
    ...mapState('checkout', [
      'payment',
      'quote',
      'data',
      'paymentLoading',
      'passengers'
    ]),
    ...mapGetters('checkout', [
      'currency',
      'isReservation',
      'passengersHaveMobilityLimitations',
      'paymentFormIsValid',
      'paymentData'
    ]),

    amountsToPay() {
      let fullBalance = {
        value: this.payment.data.fullBalance,
        label: `
          Full Balance
          <span class="payment__price">${this.currency(
            this.payment.data.fullBalance
          )}</span>
        `
      };

      if (this.data.brandNameFormatted === Brand.Travelsphere) {
        fullBalance = {
        value: this.payment.data.fullBalance,
        label: `
          Full Balance
          <span class="payment__price payment__priceTs">${this.currency(
            this.payment.data.fullBalance
          )}</span>
        `
      };
      }

      const amountsToPay = [];
      amountsToPay.push(fullBalance);

      // show deposit payment if its allowed
      if (this.payment.data.depositAmount !== this.payment.data.fullBalance) {
        let depositAmount = {
          value: this.payment.data.depositAmount,
          label: `
            Deposit Only
            <span class="payment__price">${this.currency(
              this.payment.data.depositAmount
            )}</span>
            <span class="payment__remaining">(${this.currency(
              this.payment.data.fullBalance - this.payment.data.depositAmount
            )} to pay)</span>
            <span class="payment__due-date">Due ${formatDate(
              this.payment.data.dueDate
            )}</span>
          `
        };

        if (this.data.brandNameFormatted === Brand.Travelsphere) {
          depositAmount = {
          value: this.payment.data.depositAmount,
          label: `
            Deposit Only
            <span class="payment__price payment__priceTs">${this.currency(
              this.payment.data.depositAmount
            )}</span>
            <span class="payment__remaining">(${this.currency(
              this.payment.data.fullBalance - this.payment.data.depositAmount
            )} to pay)</span>
            <span class="payment__due-date">Due ${formatDate(
              this.payment.data.dueDate
            )}</span>
          `
        };
        }
        amountsToPay.push(depositAmount);
      }

      return amountsToPay;
    },

    showButton() {
      if (
        this.payment.summaryView ||
        (!this.payment.canPay && this.payment.hasBeenSubmitted)
      ) {
        return false;
      }
      return true;
    }
  },

  methods: {
    save() {
      this.$store.commit('setHasBeenSubmitted', this.vuexPath);

      if (this.payment.canPay && this.paymentFormIsValid) {
        this.$store.commit('setSummaryView', this.vuexPath);
        this.$store.commit("checkout/setTransactionType", this.getTransactionType());
        this.$store.commit("checkout/setPaymentType", this.getCardName());
        scrollToElement(this.$refs.container, true);

        var dataLayerItem = JSON.parse(JSON.stringify({
          brand: this.data.brand,
          quote: this.quote,
          paymentData: this.paymentData,
          payment_type: this.getCardName(),
          transaction_type: this.getTransactionType(),
          data: this.$store.state.checkout
        }));

        GTM.addPaymentInfo(dataLayerItem);
      }
    },

    edit() {
      this.$store.commit('unsetSummaryView', this.vuexPath);
    },

    getCardName() {
      return this.payment.data.cardTypes.find(
        card => card.id === this.paymentData.cardTypeId
      ).name;
    },

    getTransactionType() {
      const isDepositOnly = this.payment.data.depositAmount !== this.payment.data.fullBalance;
 
      if (isDepositOnly) {
        return 'Deposit Only';
      } else {
        return 'Full Payment';
      }
    },

  }
};
</script>
